import React, { useState, useEffect, useCallback } from "react";

// reactstrap components
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Form,
  Input,
  InputGroupText,
  InputGroup,
  Container,
  Col,
  Row,
} from "reactstrap";
import { useHistory } from "react-router-dom";
import LoadingOverlay from "react-loading-overlay";
import { useSession } from "context/SessionContext";

function Login() {
  const [userName, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const [terms, setTerms] = useState("");
  const [mandatoryAgreement, setMandatoryAgreement] = useState("");
  const [termsAgreed, setTermsAgreed] = useState(false);

  const [errorMsg, setErrorMsg] = useState([]);
  const [showLoading, setShowLoading] = useState(false);

  const { setSessionData, clearSession } = useSession();

  const history = useHistory();

  useEffect(() => {
    if (sessionStorage.getItem("sessionId") != null) {
      performLogout();
    }
  }, []);

  const redirectToDashboard = () => {
    const location = {
      pathname: "/admin/dashboard",
    };
    history.push(location);
  };

  const redirectToLicense = () => {
    const location = {
      pathname: "/auth/terms",
    };
    history.push(location);
  };

  const performLogin = useCallback(() => {
    if (mandatoryAgreement && !termsAgreed) {
      setErrorMsg("You must agree to the terms and conditions");
      return;
    }

    setShowLoading(true);
    fetch("/api/v1/auth/login", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Basic " + btoa(`${userName}:${password}`),
      },
    })
      .then((res) => {
        if (res.status != 200) {
          setErrorMsg("Authentication failed: " + res.statusText);
          return Promise.reject("Authentication failed");
        }
        return res.json();
      })
      .then((data) => {
        if (data.data.privilegeLevel < 5) {
          setShowLoading(false);
          setErrorMsg("Authentication failed: Insufficient privilege level");
          return Promise.reject("Authentication failed");
        }

        setShowLoading(false);
        try {
          setSessionData(data.data);
        } catch (err) {
          console.error("setSessionData error:", err);
        }
        if (data.data.termsAgreed) {
          redirectToDashboard();
        } else {
          redirectToLicense();
        }
      })
      .catch((err) => {
        console.error(err);
        setShowLoading(false);
      });
  }, [userName, password, mandatoryAgreement, termsAgreed]);

  const performLogout = () => {
    fetch("/api/v1/auth/logout", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        sessionId: sessionStorage.getItem("sessionId"),
      },
    })
      .then((res) => {
        sessionStorage.clear();
        clearSession();
      })
      .catch((err) => {
        console.log(err.message);
        sessionStorage.clear();
        clearSession();
      });
  };

  React.useEffect(() => {
    document.body.classList.toggle("login-page");
    return function cleanup() {
      document.body.classList.toggle("login-page");
    };
  });

  React.useEffect(() => {
    fetch("/api/v1/setting/terms", {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        setTerms(data.data.terms);
        setMandatoryAgreement(data.data.mandatoryTermsAgreement);
      })
      .catch((err) => {
        console.log(err.message);
      });
  }, []);

  useEffect(() => {
    function handleKeyPress(event) {
      if (event.key === "Enter") {
        performLogin();
      }
    }

    document.addEventListener("keypress", handleKeyPress);

    return () => {
      document.removeEventListener("keypress", handleKeyPress);
    };
  }, [performLogin]);

  return (
    <div className="login-page">
      <LoadingOverlay
        active={showLoading}
        spinner
        text="Loading"
        className={showLoading ? "loading" : ""}
      />
      <Container>
        <Row>
          <Col md="8" className="loginInput">
            <Form action="" className="form" method="">
              <Card className="card-login">
                <CardHeader>
                  <CardHeader>
                    <h3 className="header text-center">Virtuoso</h3>
                  </CardHeader>
                </CardHeader>
                <CardBody>
                  {errorMsg.length > 0 ? (
                    <div
                      style={{
                        backgroundColor: "#FFC0A4",
                        border: "1px solid #ef8157",
                        color: "#66615b",
                        paddingTop: "15px",
                        marginBottom: "15px",
                      }}
                    >
                      <ul>
                        <li>{errorMsg}</li>
                      </ul>
                    </div>
                  ) : (
                    ""
                  )}
                  {terms ? (
                    <>
                      <div
                        style={{
                          maxHeight: "300px",
                          overflowY: "auto",
                          padding: "15px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          whiteSpace: "pre-wrap",
                          backgroundColor: "#f9f9f9",
                          marginBottom: "15px",
                          width: "100%",
                          boxSizing: "border-box",
                          fontSize: "14px",
                          lineHeight: "1.5",
                        }}
                      >
                        {terms}
                      </div>
                      {mandatoryAgreement && (
                        <div style={{ marginBottom: "10px" }}>
                          <Input
                            type="checkbox"
                            id="termsAgreement"
                            checked={termsAgreed}
                            onChange={(e) => setTermsAgreed(e.target.checked)}
                          />
                          <label
                            htmlFor="termsAgreement"
                            style={{ marginLeft: "10px" }}
                          >
                            I agree to the terms and conditions
                          </label>
                        </div>
                      )}
                    </>
                  ) : (
                    ""
                  )}

                  <InputGroup
                    style={{
                      maxWidth: "500px",
                      margin: "0 auto",
                      paddingBottom: "10px",
                    }}
                  >
                    <InputGroupText>
                      <i className="nc-icon nc-single-02" />
                    </InputGroupText>
                    <Input
                      placeholder="User Name..."
                      type="text"
                      id="username"
                      name="username"
                      value={userName}
                      onChange={(e) => setUsername(e.target.value)}
                    />
                  </InputGroup>
                  <InputGroup
                    style={{
                      maxWidth: "500px",
                      margin: "0 auto",
                      marginBottom: "10px",
                    }}
                  >
                    <InputGroupText>
                      <i className="nc-icon nc-key-25" />
                    </InputGroupText>
                    <Input
                      placeholder="Password"
                      type="password"
                      autoComplete="off"
                      id="password"
                      name="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </InputGroup>
                </CardBody>
                <CardFooter>
                  <Button
                    block
                    className="btn-round mb-3"
                    color="info"
                    onClick={performLogin}
                  >
                    Login
                  </Button>
                </CardFooter>
              </Card>
            </Form>
          </Col>
        </Row>
      </Container>
      <div
        className="full-page-background"
        style={{
          backgroundImage: `url(${require("assets/img/bg/fabio-mangione.jpg")})`,
        }}
      />
    </div>
  );
}

export default Login;
